import React, { useEffect, useRef, useState } from 'react';

// Cohesive psytrance divider: orange -> green signal line with a glowing center node
// that shimmers in an orange<->green rhythm once it scrolls into view.
export const SectionDivider = () => {
  const ref = useRef(null);
  const lineRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Subtle scroll parallax: the line drifts vertically based on viewport position
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const el = ref.current;
        const line = lineRef.current;
        if (!el || !line) return;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const frac = (center - window.innerHeight / 2) / window.innerHeight; // ~ -0.5..0.5
        line.style.transform = `translateY(${(frac * -22).toFixed(1)}px)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full py-10 flex items-center justify-center overflow-hidden bg-black"
      data-testid="section-divider"
      aria-hidden="true"
    >
      <div
        className="h-px w-4/5 max-w-2xl origin-center transition-all duration-1000 ease-out"
        style={{
          transform: inView ? 'scaleX(1)' : 'scaleX(0)',
          opacity: inView ? 1 : 0,
        }}
      >
        <div
          ref={lineRef}
          data-testid="section-divider-line"
          className="h-px w-full will-change-transform"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(249,115,22,0.55) 35%, rgba(191,255,0,0.9) 50%, rgba(34,197,94,0.55) 65%, transparent 100%)',
          }}
        />
      </div>
      <div
        className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-2.5 transition-all duration-700 ${
          inView ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        }`}
      >
        <span
          className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"
          style={{ boxShadow: '0 0 12px 2px rgba(249,115,22,0.7)' }}
        />
        <span
          className={`w-3 h-3 rotate-45 ${
            inView
              ? 'divider-node-active'
              : 'bg-gradient-to-br from-orange-500 to-green-500'
          }`}
          data-testid="section-divider-node"
          style={inView ? undefined : { boxShadow: '0 0 18px 3px rgba(191,255,0,0.55)' }}
        />
        <span
          className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"
          style={{ boxShadow: '0 0 12px 2px rgba(34,197,94,0.7)', animationDelay: '0.4s' }}
        />
      </div>
    </div>
  );
};
